import { useState, useCallback } from 'react';
import { FilePdfOutlined, FileOutlined } from '@ant-design/icons';
import { API_PATH } from 'config';

/**
 * Hook reutilizable para el manejo de archivos
 * Proporciona funcionalidades comunes como preview, descarga y utilidades
 */
const useFiles = () => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  /**
   * Obtiene el icono apropiado según el tipo de archivo
   * @param {string} fileType - Tipo MIME del archivo
   * @returns {JSX.Element|null} - Icono correspondiente o null para imágenes
   */
  const getFileIcon = useCallback((fileType) => {
    if (fileType.startsWith('image')) {
      return null;
    } else if (fileType === 'application/pdf') {
      return <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />;
    } else {
      return <FileOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
    }
  }, []);

  /**
   * Verifica si el archivo es una imagen
   * @param {string} fileType - Tipo MIME del archivo
   * @returns {boolean} - true si es imagen
   */
  const isImage = useCallback((fileType) => {
    return fileType.startsWith('image');
  }, []);

  /**
   * Genera la URL de descarga para un archivo
   * @param {string|number} fileId - ID del archivo
   * @returns {string} - URL completa de descarga
   */
  const getDownloadUrl = useCallback((fileId) => {
    return `${API_PATH}/upload/file/${fileId}/download`;
  }, []);

  /**
   * Maneja la preview de imágenes
   * @param {Object} file - Objeto del archivo con propiedad id
   */
  const handlePreview = useCallback(
    (file) => {
      const imageUrl = getDownloadUrl(file.id);
      setPreviewImage(imageUrl);
      setPreviewVisible(true);
    },
    [getDownloadUrl]
  );

  /**
   * Cierra el preview de imagen
   */
  const closePreview = useCallback(() => {
    setPreviewVisible(false);
    setPreviewImage('');
  }, []);

  /**
   * Genera propiedades para el componente Image de Ant Design
   * @returns {Object} - Props para el componente Image
   */
  const getImagePreviewProps = useCallback(() => {
    return {
      preview: {
        visible: previewVisible,
        onVisibleChange: (value) => {
          if (!value) {
            closePreview();
          }
        },
      },
      src: previewImage,
      onClick: closePreview,
      style: { display: 'none' },
    };
  }, [previewVisible, previewImage, closePreview]);

  /**
   * Genera propiedades para el Avatar según el tipo de archivo
   * @param {Object} file - Objeto del archivo
   * @param {string} file.file_type - Tipo MIME del archivo
   * @param {string|number} file.id - ID del archivo
   * @returns {Object} - Props para el componente Avatar
   */
  const getAvatarProps = useCallback(
    (file) => {
      if (isImage(file.file_type)) {
        return {
          src: getDownloadUrl(file.id),
          shape: 'square',
          size: 'large',
        };
      } else {
        return {
          icon: getFileIcon(file.file_type),
          shape: 'square',
          style: { backgroundColor: '#fde3cf' },
        };
      }
    },
    [isImage, getFileIcon, getDownloadUrl]
  );

  return {
    // Estados
    previewVisible,
    previewImage,

    // Funciones utilitarias
    getFileIcon,
    isImage,
    getDownloadUrl,

    // Funciones de manejo
    handlePreview,
    closePreview,
    getImagePreviewProps,
    getAvatarProps,
  };
};

export default useFiles;
